import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import AdminLayout from './components/Layout/AdminLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Rides from './pages/Rides';
import WhatsappAccounts from './pages/WhatsappAccounts';
import WhatsappOverview from './pages/WhatsappOverview';
import LiveMessages from './pages/LiveMessages';
import MessageArchive from './pages/MessageArchive';
import Leads from './pages/Leads';
import Settings from './pages/Settings';
import Drivers from './pages/Drivers';
import SupportTickets from './pages/SupportTickets';
import DataDeletionRequests from './pages/DataDeletionRequests';
import OperationsOverview from './pages/OperationsOverview';
import OperationsPlans from './pages/OperationsPlans';
import PaymentGatewaySettings from './pages/PaymentGatewaySettings';
import BillingPayments from './pages/BillingPayments';
import AiSettings from './pages/AiSettings';
import PreFilterSettings from './pages/PreFilterSettings';
import LoadingSpinner from './components/UI/LoadingSpinner';

const App: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  const canOperate = user?.role === 'admin' || user?.role === 'manager';
  const isAdmin = user?.role === 'admin';

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <AdminLayout>
      <Routes>
        <Route path="/login" element={<Navigate to="/dashboard" />} />
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Rides */}
        <Route path="/rides" element={canOperate ? <Rides /> : <Navigate to="/dashboard" />} />

        {/* WhatsApp section */}
        <Route path="/whatsapp" element={canOperate ? <WhatsappOverview /> : <Navigate to="/dashboard" />} />
        <Route path="/whatsapp/accounts" element={canOperate ? <WhatsappAccounts /> : <Navigate to="/dashboard" />} />
        <Route path="/whatsapp/live" element={canOperate ? <LiveMessages /> : <Navigate to="/dashboard" />} />
        <Route path="/whatsapp/messages" element={isAdmin ? <MessageArchive /> : <Navigate to="/dashboard" />} />
        <Route path="/whatsapp/leads" element={canOperate ? <Leads /> : <Navigate to="/dashboard" />} />

        {/* Drivers */}
        <Route path="/drivers" element={isAdmin ? <Drivers /> : <Navigate to="/dashboard" />} />

        {/* Operations section */}
        <Route path="/operations" element={isAdmin ? <OperationsOverview /> : <Navigate to="/dashboard" />} />
        <Route path="/operations/plans" element={isAdmin ? <OperationsPlans /> : <Navigate to="/dashboard" />} />
        <Route path="/operations/payments" element={isAdmin ? <PaymentGatewaySettings /> : <Navigate to="/dashboard" />} />
        <Route path="/operations/billing" element={isAdmin ? <BillingPayments /> : <Navigate to="/dashboard" />} />
        <Route path="/operations/ai" element={isAdmin ? <AiSettings /> : <Navigate to="/dashboard" />} />
        <Route path="/operations/prefilters" element={isAdmin ? <PreFilterSettings /> : <Navigate to="/dashboard" />} />

        {/* Support & admin */}
        <Route path="/support-tickets" element={canOperate ? <SupportTickets /> : <Navigate to="/dashboard" />} />
        <Route path="/data-deletion-requests" element={isAdmin ? <DataDeletionRequests /> : <Navigate to="/dashboard" />} />
        <Route path="/settings" element={<Settings />} />

        {/* Legacy redirects — old routes kept working */}
        <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
        <Route path="/live-messages" element={<Navigate to="/whatsapp/live" replace />} />
        <Route path="/messages" element={<Navigate to="/whatsapp/messages" replace />} />
        <Route path="/leads" element={<Navigate to="/whatsapp/leads" replace />} />
        <Route path="/users" element={<Navigate to={isAdmin ? '/drivers' : '/dashboard'} replace />} />

        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </AdminLayout>
  );
};

export default App;
